import {
  Box,
  ButtonBase,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import PL from '@/assets/icons/flags/pl.png';
import UnitedKingdom from '@/assets/icons/flags/united-kingdom.png';
import { TopBarLanguageChangeLanguageIconStyles } from '@/components/layout/topBar/components/TopBarLanguageChange/style/TopBarLanguageChange.style.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type OptionType = {
  label: React.ReactNode;
  value: string;
};

interface Props {
  variant?: 'icon' | 'menuItem';
}

export const TopBarLanguageChange: React.FC<Props> = ({ variant = 'icon' }) => {
  const { i18n } = useTranslation();
  const { t } = useTranslationWithPrefix('topBar.lng');
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChangeLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (language: string) => async () => {
    await i18n.changeLanguage(language);
    handleChangeLanguageMenuClose();
    location.reload();
  };

  const languageOptions: OptionType[] = [
    {
      label: (
        <>
          <Box
            alt={t('english')}
            component="img"
            src={UnitedKingdom}
            sx={{ height: '20px', mr: 1, width: '32px' }}
          />
          <Typography>{t('english')}</Typography>
        </>
      ),
      value: 'en',
    },
    {
      label: (
        <>
          <Box
            alt={t('polish')}
            component="img"
            src={PL}
            sx={{ height: '20px', mr: 1, width: '32px' }}
          />
          <Typography>{t('polish')}</Typography>
        </>
      ),
      value: 'pl',
    },
  ];

  const returnSelectedLanguagesFlag = () => {
    switch (i18n.language) {
      case 'en':
        return UnitedKingdom;
      case 'pl':
        return PL;
      default:
        return UnitedKingdom;
    }
  };

  if (variant === 'menuItem') {
    return (
      <Stack direction="row" spacing={0.5}>
        {languageOptions.map(option => (
          <ButtonBase
            key={option.value}
            onClick={handleLanguageChange(option.value)}
            sx={{
              '&:hover': {
                backgroundColor: theme.colors.bgHover,
                opacity: 1,
              },
              background: i18n.language === option.value ? theme.colors.accentBg : 'none',
              border: `1px solid ${
                i18n.language === option.value ? theme.colors.accentBorder : theme.colors.border
              }`,
              borderRadius: '999px',
              height: 28,
              opacity: i18n.language === option.value ? 1 : 0.5,
              overflow: 'hidden',
              px: 0.75,
              transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            <Box
              alt={option.value}
              component="img"
              src={option.value === 'pl' ? PL : UnitedKingdom}
              sx={{ borderRadius: '50%', height: 16, objectFit: 'cover', width: 16 }}
            />
          </ButtonBase>
        ))}
      </Stack>
    );
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          '&:hover': {
            backgroundColor: theme.colors.bgCard3,
          },
          '&.MuiIconButton-root': {
            border: 'none',
          },
          backgroundColor: open ? theme.colors.bgCard3 : 'transparent',
          borderRadius: '50%',
          height: '40px',
          padding: '8px',
          transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          width: '40px',
        }}
      >
        <Box sx={TopBarLanguageChangeLanguageIconStyles}>
          <Box
            alt={t('selectedLanguage')}
            component="img"
            src={returnSelectedLanguagesFlag()}
            sx={{
              height: '100%',
              objectFit: 'cover',
              width: '100%',
            }}
          />
        </Box>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        onClose={handleChangeLanguageMenuClose}
        open={open}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: theme.colors.bgCard,
              backgroundImage: 'none',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '16px',
              boxShadow: theme.colors.shadowCard,
              mt: 1.5,
              overflow: 'hidden',
            },
          },
        }}
      >
        {languageOptions.map(option => (
          <MenuItem
            key={option.value}
            onClick={handleLanguageChange(option.value)}
            sx={{
              '&:hover': { backgroundColor: theme.colors.bgHover },
              color: theme.colors.textPrimary,
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
